import React from "react";
import PropTypes from "prop-types";
import TemplateBodyPreview from "./TemplateBodyPreview";
import styles from "./TemplateCard.module.css";

/**
 * Card de um modelo de mensagem. Renderiza badge de status humanizado, preview
 * da mensagem, data de criação e ações que variam por status.
 *
 * Estados de status do backend: DRAFT | PENDING | APPROVED | REJECTED.
 * As ações são callbacks recebidos via props; o card não chama a API.
 *
 * `busyAction` indica qual ação está em andamento neste card (ex.: "sync",
 * "submit", "activate") para exibir spinner/desabilitar — controlado pelo pai.
 */

/** Resolve a aparência do badge a partir do status + isActive. */
function getBadge(status, isActive) {
  if (status === "APPROVED" && isActive) {
    return { label: "🟢 Ativo — Em uso", variant: "active" };
  }
  switch (status) {
    case "APPROVED":
      return { label: "✅ Aprovado", variant: "approved" };
    case "PENDING":
      return { label: "⏳ Aguardando aprovação", variant: "pending" };
    case "REJECTED":
      return { label: "❌ Reprovado", variant: "rejected" };
    case "DRAFT":
    default:
      return { label: "📝 Rascunho", variant: "draft" };
  }
}

/** Formata "createdAt" para dd/mm/aaaa em pt-BR. */
function formatCreatedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function TemplateCard({
  template,
  busyAction = null,
  onSubmit,
  onSync,
  onActivate,
  onEdit,
  onDelete,
}) {
  const { status, isActive, displayName, bodyText, rejectionReason, warning } =
    template;
  const badge = getBadge(status, isActive);
  const createdAt = formatCreatedAt(template.createdAt);
  const isBusy = (action) => busyAction === action;
  const anyBusy = busyAction !== null;

  return (
    <li
      className={`${styles.card} ${isActive ? styles.cardActive : ""}`.trim()}
    >
      <div className={styles.topRow}>
        <span className={`${styles.badge} ${styles[badge.variant]}`}>
          {badge.label}
        </span>
        {createdAt && (
          <span className={styles.createdAt}>Criado em {createdAt}</span>
        )}
      </div>

      <h3 className={styles.name}>{displayName}</h3>

      {/* Aviso de criação sem WhatsApp conectado (campo `warning`). */}
      {warning && (
        <div className={styles.warningBanner} role="status">
          <span aria-hidden="true">⚠️</span>
          <span>{warning}</span>
        </div>
      )}

      {/* Motivo da rejeição em destaque. */}
      {status === "REJECTED" && rejectionReason && (
        <div className={styles.rejectionBox} role="alert">
          <span className={styles.rejectionLabel}>Motivo da reprovação:</span>{" "}
          {rejectionReason}
        </div>
      )}

      <TemplateBodyPreview text={bodyText} />

      <div className={styles.actions}>
        {status === "DRAFT" && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSubmit}
            disabled={anyBusy}
          >
            {isBusy("submit") ? "Enviando..." : "Enviar para aprovação"}
          </button>
        )}

        {status === "PENDING" && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSync}
            disabled={anyBusy}
          >
            {isBusy("sync") ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Verificando...
              </>
            ) : (
              "Verificar status"
            )}
          </button>
        )}

        {status === "APPROVED" && !isActive && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onActivate}
            disabled={anyBusy}
          >
            {isBusy("activate") ? "Ativando..." : "Ativar este modelo"}
          </button>
        )}

        {status === "REJECTED" && (
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onEdit}
            disabled={anyBusy}
          >
            Corrigir e reenviar
          </button>
        )}

        <button
          type="button"
          className={styles.btnDanger}
          onClick={onDelete}
          disabled={anyBusy}
        >
          {isBusy("delete") ? "Excluindo..." : "Excluir"}
        </button>
      </div>
    </li>
  );
}

TemplateCard.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayName: PropTypes.string.isRequired,
    bodyText: PropTypes.string,
    status: PropTypes.oneOf(["DRAFT", "PENDING", "APPROVED", "REJECTED"])
      .isRequired,
    isActive: PropTypes.bool,
    rejectionReason: PropTypes.string,
    createdAt: PropTypes.string,
    /** Aviso transitório (ex.: salvo como rascunho sem WhatsApp conectado). */
    warning: PropTypes.string,
  }).isRequired,
  /** Ação em andamento neste card: "submit"|"sync"|"activate"|"delete"|null. */
  busyAction: PropTypes.oneOf(["submit", "sync", "activate", "delete"]),
  onSubmit: PropTypes.func.isRequired,
  onSync: PropTypes.func.isRequired,
  onActivate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};
